/**
 * EPIC-DM01-G: Share Card Component (Redesigned)
 * 5 platform-specific share card templates
 *
 * Design Reference: Figma Share Card Templates
 * No PII (no name, email) — only swing insights
 *
 * Formats:
 *  - instagram-feed  : 1080x1080 (1:1)
 *  - instagram-story : 1080x1920 (9:16)
 *  - facebook        : 1200x630
 *  - twitter         : 1200x675
 *  - linkedin        : 1200x627
 */

import { cn } from '@/lib/utils';

export type ShareFormat =
  | 'instagram-feed'
  | 'instagram-story'
  | 'facebook'
  | 'twitter'
  | 'linkedin';

export const SHARE_FORMATS: {
  id: ShareFormat;
  label: string;
  short: string;
  width: number;
  height: number;
  aspect: string;
}[] = [
  { id: 'instagram-feed', label: 'Instagram Feed', short: 'Feed', width: 1080, height: 1080, aspect: 'aspect-square' },
  { id: 'instagram-story', label: 'Instagram Story', short: 'Story', width: 1080, height: 1920, aspect: 'aspect-[9/16]' },
  { id: 'facebook', label: 'Facebook', short: 'FB', width: 1200, height: 630, aspect: 'aspect-[1200/630]' },
  { id: 'twitter', label: 'Twitter / X', short: 'X', width: 1200, height: 675, aspect: 'aspect-[1200/675]' },
  { id: 'linkedin', label: 'LinkedIn', short: 'LI', width: 1200, height: 627, aspect: 'aspect-[1200/627]' },
];

export interface ShareCardProps {
  format: ShareFormat | '1:1' | '9:16';
  insight?: string;
  focusArea?: string;
  overallScore?: number;
  className?: string;
}

function normalizeFormat(f: ShareCardProps['format']): ShareFormat {
  if (f === '1:1') return 'instagram-feed';
  if (f === '9:16') return 'instagram-story';
  return f;
}

function getAspect(format: ShareFormat): string {
  return SHARE_FORMATS.find((f) => f.id === format)?.aspect ?? 'aspect-square';
}

function isVertical(format: ShareFormat): boolean {
  return format === 'instagram-story';
}

function isWide(format: ShareFormat): boolean {
  return ['facebook', 'twitter', 'linkedin'].includes(format);
}

export function ShareCard({
  format: rawFormat,
  insight = 'Your swing has been analyzed!',
  focusArea = 'Swing Improvement',
  overallScore,
  className,
}: ShareCardProps) {
  const format = normalizeFormat(rawFormat);
  const vertical = isVertical(format);
  const wide = isWide(format);

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
        'border border-slate-700/50 shadow-2xl',
        getAspect(format),
        className
      )}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(13,92,43,0.5),transparent_40%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(13,92,43,0.4),transparent_50%)]" />
      </div>

      {/* Corner accent */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#0D5C2B]/20 to-transparent" />

      {/* Content */}
      <div
        className={cn(
          'relative z-10 flex h-full',
          vertical ? 'flex-col items-center justify-center gap-8 p-8 text-center' : '',
          wide ? 'flex-row items-center gap-6 p-6' : '',
          format === 'instagram-feed' ? 'flex-col items-center justify-center gap-5 p-6 text-center' : ''
        )}
      >
        {/* Left / top section */}
        <div className={cn(wide ? 'flex-1 min-w-0' : '', wide ? 'text-left' : 'text-center')}>
          {/* Brand */}
          <div className={cn(
            'font-bold tracking-[0.2em] uppercase text-[#22c55e]',
            vertical ? 'text-sm mb-2' : wide ? 'text-[10px] mb-3' : 'text-xs mb-1'
          )}>
            No Doubles Golf Co
          </div>

          {/* "Swing Snapshot" label */}
          <div className={cn(
            'text-slate-400 uppercase tracking-wider',
            vertical ? 'text-xs mb-6' : wide ? 'text-[9px] mb-3' : 'text-[10px] mb-3'
          )}>
            Swing Snapshot
          </div>

          {/* Score */}
          {overallScore !== undefined && (
            <div className={cn('flex items-center', wide ? 'justify-start gap-3 mb-3' : 'justify-center flex-col mb-3')}>
              <div className={cn(
                'font-bold text-[#22c55e]',
                vertical ? 'text-6xl' : wide ? 'text-4xl' : 'text-5xl'
              )}>
                {overallScore}
              </div>
              <div className={cn('text-slate-400', vertical ? 'text-sm mt-1' : wide ? 'text-xs' : 'text-xs mt-1')}>
                Swing Score
              </div>
            </div>
          )}

          {/* Divider */}
          <div className={cn(
            'h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent',
            wide ? 'w-full my-3' : 'w-16 mx-auto my-3'
          )} />

          {/* Focus area */}
          <div>
            <div className={cn(
              'text-slate-500 uppercase tracking-widest',
              vertical ? 'text-xs mb-1' : wide ? 'text-[9px] mb-1' : 'text-[10px] mb-1'
            )}>
              Focus Area
            </div>
            <div className={cn(
              'font-semibold text-white',
              vertical ? 'text-2xl' : wide ? 'text-lg' : 'text-xl'
            )}>
              {focusArea}
            </div>
          </div>
        </div>

        {/* Right section (wide formats) or bottom section */}
        <div className={cn(
          wide ? 'flex-shrink-0 w-[45%] text-left' : '',
          !wide ? '' : ''
        )}>
          {/* Insight */}
          <div className={cn(
            'text-slate-300 leading-relaxed',
            vertical ? 'text-base max-w-[85%] mx-auto' : wide ? 'text-sm mb-4' : 'text-sm max-w-[85%] mx-auto'
          )}>
            {insight}
          </div>

          {/* CTA */}
          {wide && (
            <div className="inline-flex items-center gap-2 bg-[#0D5C2B] text-white text-xs font-bold px-4 py-2 rounded-lg mt-2">
              Get Your Free Analysis
            </div>
          )}
        </div>

        {/* Tagline (non-wide) */}
        {!wide && (
          <div className={cn('text-xs text-slate-500', vertical ? 'mt-4' : 'mt-2')}>
            AI-Powered Swing Analysis &middot; nodoublesgolfco.com
          </div>
        )}
      </div>

      {/* Bottom bar */}
      {wide && (
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-6 py-3 bg-black/40">
          <span className="text-[10px] text-slate-400">AI-Powered Swing Analysis</span>
          <span className="text-[10px] text-[#22c55e] font-semibold">nodoublesgolfco.com</span>
        </div>
      )}

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#0D5C2B] to-transparent opacity-60" />
    </div>
  );
}

/**
 * Share Card Preview wrapper with format selector
 * Updated to support all 5 platform formats (backwards-compatible with '1:1' / '9:16')
 */
export interface ShareCardPreviewProps {
  format: ShareCardProps['format'];
  insight?: string;
  focusArea?: string;
  overallScore?: number;
  onFormatChange?: (format: ShareFormat) => void;
}

export function ShareCardPreview({
  format: rawFormat,
  insight,
  focusArea,
  overallScore,
  onFormatChange,
}: ShareCardPreviewProps) {
  const format = normalizeFormat(rawFormat);

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Format selector */}
      {onFormatChange && (
        <div className="flex flex-wrap gap-1.5 p-1 bg-demo-surface-alt rounded-xl justify-center">
          {SHARE_FORMATS.map((f) => (
            <button
              key={f.id}
              onClick={() => onFormatChange(f.id)}
              className={cn(
                'px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors',
                format === f.id
                  ? 'bg-white text-demo-text shadow-sm'
                  : 'text-demo-text-muted hover:text-demo-text'
              )}
            >
              {f.short}
            </button>
          ))}
        </div>
      )}

      {/* Card preview */}
      <div
        className={cn(
          'transition-all duration-300',
          isVertical(format) ? 'w-40' : isWide(format) ? 'w-72' : 'w-56'
        )}
      >
        <ShareCard
          format={format}
          insight={insight}
          focusArea={focusArea}
          overallScore={overallScore}
        />
      </div>

      {/* Dimensions label */}
      <div className="text-xs text-demo-text-muted">
        {SHARE_FORMATS.find((f) => f.id === format)?.width}x
        {SHARE_FORMATS.find((f) => f.id === format)?.height}
      </div>
    </div>
  );
}
